import {
  getAccountDetailsAppAccountsAccountsAccountIdGetOptions,
  getAccountDetailsAppAccountsAccountsAccountIdGetQueryKey,
  getOtherLocationsAppAccountsAccountsAccountIdOtherLocationsGetInfiniteOptions,
  getOtherLocationsAppAccountsAccountsAccountIdOtherLocationsGetInfiniteQueryKey,
} from '@/client/@tanstack/react-query.gen'
import { createAccountFromCompanyIdAppAccountsQuickCreateFromCompanyCompanyIdPost } from '@/client/sdk.gen'
import type { AccountLocation } from '@/client/types.gen'
import { ClusterMap } from '@/components/map/cluster-map'
import type { ClusterColorMode } from '@/lib/triage-score-colors'
import { SkeletonList } from '@/components/skeleton-common'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TabsContent } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { googleMapsUrl } from '@/lib/maps'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, useNavigate } from '@tanstack/react-router'
import { Check, ChevronDown, ExternalLink, Loader2, MapPin, Plus } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

const routeApi = getRouteApi('/_authed/_user/accounts/$accountId')

const LOCATIONS_PAGE_SIZE = 50

function getIconColor(isSelected: boolean, isCurrent: boolean, hasAccount: boolean): string {
  if (isSelected)
    return 'text-primary'
  if (isCurrent)
    return 'text-amber-500'
  if (hasAccount)
    return 'text-muted-foreground'
  return 'text-muted-foreground/50'
}

function getTitleColor(isSelected: boolean, isCurrent: boolean): string {
  if (isSelected)
    return 'text-primary'
  if (isCurrent)
    return 'text-amber-600 dark:text-amber-400'
  return 'text-foreground'
}

export const Route = createFileRoute(
  '/_authed/_user/accounts/$accountId/locations',
)({
  component: LocationMapSection,
})

function LocationMapSection() {
  const { accountId } = routeApi.useParams()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [addedCompanyIds, setAddedCompanyIds] = useState<Set<number>>(new Set())
  const [pendingCompanyIds, setPendingCompanyIds] = useState<Set<number>>(new Set())

  const { data: accountDetails, isLoading, error } = useQuery(
    getAccountDetailsAppAccountsAccountsAccountIdGetOptions({
      path: { account_id: accountId },
    }),
  )

  const {
    data: otherLocationsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...getOtherLocationsAppAccountsAccountsAccountIdOtherLocationsGetInfiniteOptions({
      path: { account_id: accountId },
      query: { limit: LOCATIONS_PAGE_SIZE },
    }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.locations.length, 0)
      if (totalFetched >= lastPage.totalCount)
        return
      return totalFetched
    },
  })

  const [selectedLocation, setSelectedLocation] = useState<AccountLocation | undefined>()
  const [colorMode, setColorMode] = useState<ClusterColorMode>('mean')

  // Mutation for adding accounts
  const addAccountMutation = useMutation({
    mutationFn: async (companyId: number) => {
      const response = await createAccountFromCompanyIdAppAccountsQuickCreateFromCompanyCompanyIdPost({
        path: { company_id: companyId },
      })
      return { companyId, data: response.data }
    },
    onMutate: (companyId) => {
      setPendingCompanyIds(prev => new Set(prev).add(companyId))
    },
    onSuccess: ({ companyId, data }) => {
      setPendingCompanyIds((prev) => {
        const next = new Set(prev)
        next.delete(companyId)
        return next
      })
      setAddedCompanyIds(prev => new Set(prev).add(companyId))
      queryClient.invalidateQueries({ queryKey: getAccountDetailsAppAccountsAccountsAccountIdGetQueryKey({ path: { account_id: accountId } }) })
      queryClient.invalidateQueries({ queryKey: getOtherLocationsAppAccountsAccountsAccountIdOtherLocationsGetInfiniteQueryKey({ path: { account_id: accountId } }) })
      // Show toast with optional link to new account
      const newAccountId = data?.[0]?.id_
      toast({
        title: 'Account added successfully',
        description: newAccountId ? 'Click to view the new account' : undefined,
        action: newAccountId
          ? (
              <button
                type="button"
                className="text-primary underline"
                onClick={() => navigate({
                  to: '/accounts/$accountId/overview',
                  params: { accountId: newAccountId },
                })}
              >
                View
              </button>
            )
          : undefined,
      })
    },
    onError: (_, companyId) => {
      setPendingCompanyIds((prev) => {
        const next = new Set(prev)
        next.delete(companyId)
        return next
      })
      toast({ title: 'Failed to add account', variant: 'destructive' })
    },
  })

  const addAccountMutate = addAccountMutation.mutate
  const handleAddAccount = useCallback((e: React.MouseEvent, companyId: number) => {
    e.stopPropagation()
    addAccountMutate(companyId)
  }, [addAccountMutate])

  const allOtherLocations = useMemo(() => {
    return otherLocationsData?.pages.flatMap(page => page.locations) ?? []
  }, [otherLocationsData])

  const totalOtherLocationsCount = otherLocationsData?.pages[0]?.totalCount ?? 0

  const location = accountDetails?.location
  const locations = useMemo(() => {
    const allLocations: AccountLocation[] = []

    if (location?.latitude !== undefined && location?.longitude !== undefined) {
      allLocations.push(location)
    }

    allLocations.push(
      ...allOtherLocations.filter(
        loc => loc.latitude !== undefined && loc.longitude !== undefined,
      ),
    )

    return allLocations
  }, [location, allOtherLocations])

  // Build GeoJSON for the map - include any location with coordinates. Sibling
  // "other locations" without an account in this org have accountId=null and
  // should still render (shown in sidebar with an "Add to Accounts" action).
  const geojson = useMemo(() => {
    const features = locations
      .filter(
        (loc): loc is typeof loc & { longitude: number, latitude: number } =>
          loc.longitude !== undefined
          && loc.longitude !== null
          && loc.latitude !== undefined
          && loc.latitude !== null,
      )
      .map(loc => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [loc.longitude, loc.latitude] as [number, number],
        },
        properties: {
          accountId: loc.accountId ?? undefined,
          companyId: loc.companyId,
          triageScore: loc.triageScore,
          name: loc.name,
          address: loc.address,
          ownerName: loc.ownerName,
        },
      }))
    return { type: 'FeatureCollection' as const, features }
  }, [locations])

  const handleMarkerClick = useCallback((location: AccountLocation) => {
    setSelectedLocation(location)
  }, [])

  const handleListItemClick = useCallback((location: AccountLocation) => {
    setSelectedLocation(prev => prev?.companyId === location.companyId ? undefined : location)
  }, [])

  if (isLoading) {
    return (
      <TabsContent value="locations" className="flex-1 min-h-0">
        {/* Mirrors the loaded sidebar + map split so nothing jumps when data lands. */}
        <div className="flex h-full w-full flex-col gap-4 overflow-hidden px-4 lg:px-6 py-4">
          <div className="flex flex-1 min-h-0 flex-col gap-4 lg:flex-row lg:min-h-130 lg:max-h-175">
            <div className="order-2 lg:order-1 lg:w-1/3 lg:min-w-[320px]">
              <SkeletonList items={5} />
            </div>
            <Skeleton className="order-1 min-h-64 flex-1 rounded-lg lg:order-2" />
          </div>
        </div>
      </TabsContent>
    )
  }

  if (error) {
    return (
      <TabsContent value="locations" className="flex-1">
        <div className="flex h-96 items-center justify-center text-destructive">
          Failed to load locations
        </div>
      </TabsContent>
    )
  }

  return (
    <TabsContent value="locations" className="flex-1 min-h-0">
      <div className="flex h-full w-full flex-col gap-4 overflow-hidden px-4 lg:px-6 py-4">
        <div className="flex flex-1 min-h-0 flex-col gap-4 lg:flex-row lg:min-h-130 lg:max-h-175">
          {/* Sidebar */}
          <div className="order-2 flex flex-col min-h-0 max-lg:flex-1 lg:order-1 lg:w-1/3 lg:min-w-[320px]">
            <div className="flex-1 min-h-0 space-y-2 overflow-y-auto lg:pr-2">
              {locations.map((loc) => {
                const isSelected = selectedLocation?.companyId === loc.companyId
                const isCurrent = loc.accountId === Number(accountId)
                const hasAccount = !!loc.accountId
                const isPending = pendingCompanyIds.has(loc.companyId)
                const isAdded = addedCompanyIds.has(loc.companyId)
                const showAddButton = !hasAccount && !isAdded
                return (
                  <Card
                    key={loc.companyId}
                    role="button"
                    tabIndex={0}
                    className={`w-full py-3 px-3 text-left transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-primary/5 border-primary'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleListItemClick(loc)}
                    onKeyDown={e => e.key === 'Enter' && handleListItemClick(loc)}
                  >
                    <div className="flex items-start gap-2">
                      <MapPin
                        className={`h-4 w-4 shrink-0 mt-0.5 ${getIconColor(isSelected, isCurrent, hasAccount || isAdded)}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3
                            className={`min-w-0 max-w-full text-sm font-medium truncate ${getTitleColor(isSelected, isCurrent)}`}
                          >
                            {loc.name}
                          </h3>
                          <a
                            href={googleMapsUrl(loc)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open in Google Maps"
                            className="inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            onClick={event => event.stopPropagation()}
                          >
                            <ExternalLink className="size-3.5" />
                          </a>
                          {isCurrent && (
                            <span className="shrink-0 text-xs text-amber-600">
                              (this account)
                            </span>
                          )}
                          {showAddButton && (
                            <button
                              type="button"
                              disabled={isPending}
                              onClick={e => handleAddAccount(e, loc.companyId)}
                              className="shrink-0 inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 disabled:opacity-50"
                            >
                              {isPending
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <Plus className="h-3 w-3" />}
                              {isPending ? 'Adding...' : 'Add to Accounts'}
                            </button>
                          )}
                          {isAdded && (
                            <span className="shrink-0 inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400">
                              <Check className="h-3 w-3" />
                              Added
                            </span>
                          )}
                          {hasAccount && !isCurrent && loc.triageScore !== undefined && (
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {loc.triageScore}
                              /10
                            </span>
                          )}
                        </div>
                        {loc.address && (
                          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                            {loc.address}
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                )
              })}
              {hasNextPage && (
                <button
                  type="button"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:opacity-50"
                >
                  {isFetchingNextPage
                    ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      )
                    : (
                        <>
                          <ChevronDown className="h-4 w-4" />
                          Load more locations (
                          {allOtherLocations.length}
                          {' '}
                          of
                          {' '}
                          {totalOtherLocationsCount}
                          )
                        </>
                      )}
                </button>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="order-1 h-80 max-lg:shrink-0 lg:order-2 lg:h-auto lg:flex-1 lg:min-w-[320px]">
            <ClusterMap
              geojson={geojson}
              highlightAccountId={accountId}
              selectedLocation={selectedLocation}
              countLabel="locations"
              height="100%"
              onMarkerClick={handleMarkerClick}
              onClosePopup={() => setSelectedLocation(undefined)}
              colorMode={colorMode}
              onColorModeChange={setColorMode}
            />
          </div>
        </div>
      </div>
    </TabsContent>
  )
}
